/**
 * SAY MY NAME — the working environment.
 *
 * Two parts, and THE HIERARCHY BETWEEN THEM FLIPS with the stage. Only one of
 * them is ever the place to look:
 *
 *   UNDERSTAND — the BLACK PANEL is the conversation: the agent's question is the
 *     largest thing on the screen, with its options and a growing text field. The
 *     GREEN CANVAS is quiet and does not talk: it is the live brief, reflecting
 *     what the agent has established about the customer so far, filling in row by
 *     row as they answer.
 *   DISCOVER / DECIDE — that reverses. The canvas becomes the main event, where
 *     research and candidates stream in as they are written, and the panel goes
 *     quiet: what the agent is doing now, plus the occasional real question.
 *
 * STREAMING IS REAL. Blocks arrive as `StudioFrame` children one at a time,
 * emitted by the contract's `composePartial` as the model finishes writing each
 * one (`agent-dev-server/src/surfaces/studio.ts`). The cursor is bound to
 * `userRequestPending` — the live run state — so it blinks only while generation
 * is genuinely happening and stops the moment the turn ends. It is never shown
 * over already-finished output.
 *
 * The status light is derived from that same real state plus whether a question
 * is open, never from anything the model asserts about itself.
 *
 * Mobile keeps all three jobs: presence on top, output in the middle, the
 * decision at the bottom where the thumb is.
 */
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { defineMessages, useIntl } from 'react-intl';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSendIntent } from '@/app/lib/a2ui/blocks/use-send-intent.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { useMessagingStore } from '@/app/lib/hooks';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { cn } from '@/app/lib/utils';
import { ArrowRight, Check } from 'lucide-react';
import { AgentInput } from './AgentInput.tsx';
import { StatusLight } from './StatusLight.tsx';
import { PhaseIndicator } from '../PhaseIndicator.tsx';
import { BriefDeliverable } from './BriefDeliverable.tsx';
import { InProgressBriefActions } from './InProgressBriefActions.tsx';
import {
  CanvasFieldLabel,
  CanvasFrameLabel,
  CanvasSectionLabel,
  StatusTag,
  canvasLabelClass,
  readStatus,
  type EvidenceStatus,
} from './canvas-type.tsx';
import {
  deriveStudioState,
  markStudioEngaged,
  offersKeepExploring,
  readPhaseProp,
  setStudioPhase,
  statusLightFor,
  type StudioUiState,
} from '../studio-state.ts';

const messages = defineMessages({
  agentLabel: {
    id: 'studio.agentLabel',
    defaultMessage: 'YOUR AGENT',
    description: 'Label identifying the agent panel',
  },
  working: {
    id: 'studio.status.working',
    defaultMessage: 'Working',
    description: 'Status word shown while the agent is generating',
  },
  waiting: {
    id: 'studio.status.waiting',
    defaultMessage: 'Waiting for you',
    description: 'Status word shown when the agent has finished and holds',
  },
  needsInput: {
    id: 'studio.status.needsInput',
    defaultMessage: 'Your call',
    description: 'Status word shown when a human decision is open',
  },
  replyPlaceholder: {
    id: 'studio.reply.placeholder',
    defaultMessage: 'Tell the agent…',
    description: 'Placeholder of the compact free-text field inside the agent panel',
  },
  keepExploring: {
    id: 'studio.reply.keepExploring',
    defaultMessage: 'Keep exploring',
    description: 'Quick action that hands the turn back so the agent carries on researching',
  },
  agentOutput: {
    id: 'studio.canvas.output',
    defaultMessage: 'AGENT OUTPUT',
    description: 'Small caps label in the top-left corner of the output canvas',
  },
  briefCorner: {
    id: 'studio.canvas.briefCorner',
    defaultMessage: 'YOUR BRIEF',
    description: 'Small caps label in the top-left corner of the canvas while understanding',
  },
  listening: {
    id: 'studio.canvas.listening',
    defaultMessage: 'LIVE / LISTENING',
    description: 'Small caps label in the canvas corner while the agent is still understanding',
  },
  liveStreaming: {
    id: 'studio.canvas.live',
    defaultMessage: 'LIVE / STREAMING',
    description: 'Small caps label shown in the canvas corner while output is streaming',
  },
  liveIdle: {
    id: 'studio.canvas.idle',
    defaultMessage: 'LIVE / HOLDING',
    description: 'Small caps label shown in the canvas corner when nothing is streaming',
  },
  whatImSeeing: {
    id: 'studio.canvas.seeing',
    defaultMessage: "WHAT I'M SEEING",
    description: 'Small caps label above the canvas headline',
  },
  briefTitle: {
    id: 'studio.canvas.brief',
    defaultMessage: 'WHAT I UNDERSTAND SO FAR',
    description: 'Small caps label above the live brief on the canvas',
  },
  overToYou: {
    id: 'studio.panel.overToYou',
    defaultMessage: 'OVER TO YOU',
    description: 'Small caps label above the agent’s open invitation while it is still learning the brief',
  },
  briefEmpty: {
    id: 'studio.canvas.briefEmpty',
    defaultMessage: 'Your brief will build up here as we talk.',
    description: 'Quiet line on the canvas before anything has been established',
  },
  agentWriting: {
    id: 'studio.canvas.writing',
    defaultMessage: 'AGENT WRITING',
    description: 'Small caps label above the streamed output while the agent writes',
  },
  agentWrote: {
    id: 'studio.canvas.wrote',
    defaultMessage: 'AGENT OUTPUT',
    description: 'Small caps label above the streamed output once the turn has ended',
  },
  outputField: {
    id: 'studio.canvas.field',
    defaultMessage: 'SAY MY NAME / OUTPUT FIELD',
    description: 'Small caps label in the bottom-left corner of the output canvas',
  },
  realTime: {
    id: 'studio.canvas.realTime',
    defaultMessage: 'UPDATED IN REAL TIME',
    description: 'Small caps label in the bottom-right corner of the output canvas',
  },
  agentNow: {
    id: 'studio.panel.now',
    defaultMessage: 'AGENT / NOW',
    description: "Small caps label above the agent's statement in the panel",
  },
  workingOn: {
    id: 'studio.panel.workingOn',
    defaultMessage: 'WORKING ON',
    description: 'Small caps label above what the agent has underway',
  },
  yourJudgment: {
    id: 'studio.panel.judgment',
    defaultMessage: 'YOUR JUDGMENT',
    description: 'Small caps label above the question the agent needs answered',
  },
  soFar: {
    id: 'studio.soFar',
    defaultMessage: 'SO FAR',
    description: 'Heading over findings carried forward from earlier in the engagement',
  },
  loveIt: {
    id: 'studio.react.love',
    defaultMessage: 'This one',
    description: 'Reaction button: the visitor likes this candidate',
  },
  maybe: {
    id: 'studio.react.maybe',
    defaultMessage: 'Maybe',
    description: 'Reaction button: the visitor is unsure about this candidate',
  },
  notThis: {
    id: 'studio.react.no',
    defaultMessage: 'Not this',
    description: 'Reaction button: the visitor rejects this candidate',
  },
  tellMeMore: {
    id: 'studio.react.tellMeMore',
    defaultMessage: 'Tell me more',
    description: 'Button opening the full story of one candidate',
  },
  realWord: {
    id: 'studio.nameKind.realWord',
    defaultMessage: 'REAL WORD',
    description: 'Classification badge: the candidate is an existing word',
  },
  derived: {
    id: 'studio.nameKind.derived',
    defaultMessage: 'DERIVED',
    description: 'Classification badge: the candidate is built from a real root',
  },
  invented: {
    id: 'studio.nameKind.invented',
    defaultMessage: 'INVENTED',
    description: 'Classification badge: the candidate is coined with no prior meaning',
  },
  confidenceLabel: {
    id: 'studio.confidence.label',
    defaultMessage: 'CONFIDENCE',
    description: 'Small caps label before how much weight a name can carry',
  },
  confidenceHigh: {
    id: 'studio.confidence.high',
    defaultMessage: 'HIGH',
    description: 'Confidence level: every screening check came back',
  },
  confidenceMedium: {
    id: 'studio.confidence.medium',
    defaultMessage: 'MEDIUM',
    description: 'Confidence level: a check is still open',
  },
  confidenceLow: {
    id: 'studio.confidence.low',
    defaultMessage: 'LOW',
    description: 'Confidence level: something works against the name',
  },
  confidenceRuledOut: {
    id: 'studio.confidence.ruledOut',
    defaultMessage: 'NOT AVAILABLE',
    description: 'Confidence level: the name failed screening and is shown only as direction',
  },
  exploratoryOnly: {
    id: 'studio.candidate.exploratoryOnly',
    defaultMessage: 'DIRECTION ONLY',
    description: 'Badge beside a ruled-out name: shown for inspiration, not as an option',
  },
  milestonesLabel: {
    id: 'studio.panel.milestones',
    defaultMessage: 'DONE SO FAR',
    description: 'Small caps label above the work the agent has already finished this turn',
  },
  editBriefRow: {
    id: 'studio.brief.edit',
    defaultMessage: 'Correct this',
    description: 'Accessible label for the button that opens a brief row for correction',
  },
  saveBriefRow: {
    id: 'studio.brief.save',
    defaultMessage: 'Save',
    description: 'Button confirming a correction to a brief row',
  },
  cancelBriefRow: {
    id: 'studio.brief.cancel',
    defaultMessage: 'Cancel',
    description: 'Button discarding a correction to a brief row',
  },
  briefEditHint: {
    id: 'studio.brief.hint',
    defaultMessage: 'Anything I got wrong? Click it to correct me.',
    description: 'Quiet hint telling the visitor the brief rows are correctable',
  },
  settledTitle: {
    id: 'studio.canvas.settled',
    defaultMessage: 'ESTABLISHED',
    description: 'Title over the part of the canvas holding what is already agreed with the customer',
  },
  liveTitle: {
    id: 'studio.canvas.live.title',
    defaultMessage: 'IN PROGRESS',
    description: 'Title over the part of the canvas holding work that is still changing',
  },
  writeInstead: {
    id: 'studio.panel.writeInstead',
    defaultMessage: 'Or, if nothing comes to mind:',
    description: 'Quiet label demoting the preset options below the free-text field',
  },
  youFoundIt: {
    id: 'studio.selection.eyebrow',
    defaultMessage: 'YOU FOUND IT.',
    description: 'Eyebrow line above the chosen name on the final plaque',
  },
  selectedName: {
    id: 'studio.selection.label',
    defaultMessage: 'SELECTED NAME',
    description: 'Label under the chosen name on the final plaque',
  },
  nextSteps: {
    id: 'studio.selection.nextSteps',
    defaultMessage: 'WHAT TO DO NEXT',
    description: 'Title above the practical steps after a name is chosen',
  },
});

const KIND_MESSAGES = {
  'real-word': messages.realWord,
  derived: messages.derived,
  invented: messages.invented,
} as const;

const CONFIDENCE_MESSAGES = {
  high: messages.confidenceHigh,
  medium: messages.confidenceMedium,
  low: messages.confidenceLow,
  'ruled-out': messages.confidenceRuledOut,
} as const;

type ConfidenceLevel = keyof typeof CONFIDENCE_MESSAGES;

function readConfidence(value: unknown): ConfidenceLevel | undefined {
  return value === 'high' || value === 'medium' || value === 'low' || value === 'ruled-out'
    ? value
    : undefined;
}

interface EvidenceLine {
  label: string;
  value: string;
  status?: EvidenceStatus;
}

/** One row of the live brief. Same shape as an evidence row; different job. */
type BriefRow = { label: string; value: string };

interface Source {
  label: string;
  url?: string;
}

function readLines(value: unknown): EvidenceLine[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const label = str(record.label);
    const text = str(record.value);
    if (!label || !text) {
      return [];
    }
    const status = readStatus(record.status);
    return [{ label, value: text, ...(status === undefined ? {} : { status }) }];
  });
}

/** How much weight a name can carry right now. Same restrained vocabulary as the
 *  evidence tags — quiet outline when everything came back, dashed while a check
 *  is open, solid when something works against it, and solid-with-a-rule when the
 *  name is out of the running altogether. */
const ConfidenceTag: FC<{ confidence: ConfidenceLevel; className?: string }> = ({
  confidence,
  className,
}) => {
  const intl = useIntl();
  return (
    <p className={cn('flex items-center gap-2', className)}>
      <span className={canvasLabelClass('field')}>
        {intl.formatMessage(messages.confidenceLabel)}
      </span>
      <span
        className={cn(
          'rounded-[3px] px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-caps',
          confidence === 'high'
            ? 'border border-canvas-meta/30 text-canvas-meta/80'
            : confidence === 'medium'
              ? 'border border-dashed border-canvas-meta/70 text-canvas-meta'
              : confidence === 'low'
                ? 'border border-canvas-ink bg-canvas-ink text-canvas'
                : 'border border-canvas-ink/60 text-canvas-ink/60 line-through decoration-canvas-ink/60',
        )}
      >
        {intl.formatMessage(CONFIDENCE_MESSAGES[confidence])}
      </span>
    </p>
  );
};

function readSources(value: unknown): Source[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const label = str(record.label);
    if (!label) {
      return [];
    }
    const url = optStr(record.url);
    return [{ label, ...(url === undefined ? {} : { url }) }];
  });
}

/** Presence: the light, the label, and the state in words. The panel says WHAT
 *  THE AGENT IS DOING; the canvas says WHAT IT IS FINDING. They never repeat each
 *  other. */
const Presence: FC<{
  state: StudioUiState;
  activity: string;
  className?: string;
}> = ({ state, activity, className }) => {
  const intl = useIntl();
  const statusWord =
    state === 'working'
      ? intl.formatMessage(messages.working)
      : state === 'needs_input'
        ? intl.formatMessage(messages.needsInput)
        : intl.formatMessage(messages.waiting);

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      <div className="flex items-start gap-2.5">
        <StatusLight status={statusLightFor(state)} />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-3">
          <span
            className="inline-flex h-7 flex-none items-center text-[10px] font-medium tracking-caps text-muted-foreground"
            translate="no"
          >
            {intl.formatMessage(messages.agentLabel)}
          </span>
          <PhaseIndicator />
        </div>
      </div>
      <p className="min-w-0 text-xs font-medium text-panel-ink">
        {statusWord}
        {state === 'working' && activity ? (
          <span className="text-muted-foreground"> · {activity}</span>
        ) : null}
      </p>
    </div>
  );
};

/** What the agent has underway right now — shown only while it is genuinely
 *  working, and only in the panel. */
const CurrentlyLine: FC<{ currently: string }> = ({ currently }) => {
  const intl = useIntl();
  return (
    <div className="flex flex-col gap-2 border-t border-panel-line pt-6">
      <span className="text-[10px] font-semibold uppercase leading-none tracking-caps text-muted-foreground-subtle">
        {intl.formatMessage(messages.workingOn)}
      </span>
      <p aria-live="polite" className="max-w-[42ch] text-xs leading-relaxed text-muted-foreground">
        {currently}
      </p>
    </div>
  );
};

/** A compact free-text field, INSIDE the agent panel. It exists only when the
 *  ball is with the customer; while the agent works there is no input anywhere on
 *  the page, and there is never a detached composer at the bottom of the screen.
 *  It grows with what is typed, like the landing field. */
const PanelInput: FC = () => {
  const intl = useIntl();
  const sendIntent = useSendIntent();
  return (
    <AgentInput
      size="panel"
      placeholder={intl.formatMessage(messages.replyPlaceholder)}
      onSubmit={sendIntent}
    />
  );
};

/** The canvas type hierarchy and the evidence tags live in `canvas-type.tsx`, so
 *  the working blocks and the Naming Brief deliverable share ONE vocabulary. */

/** The blinking cursor: BLACK, a solid block, and mounted only while the run is
 *  genuinely in flight — so it trails the output being written and disappears the
 *  moment the turn ends. */
const Cursor: FC = () => (
  <span
    aria-hidden="true"
    className="canvas-caret ml-1 inline-block h-[1.05em] w-[0.4em] bg-canvas-meta align-text-bottom"
  />
);

/**
 * THE ANTIDOTE TO DEAD AIR.
 *
 * Research turns run for minutes. Without this the canvas sits empty behind a
 * blinking cursor and the honest reading is "it has died" — so the agent re-renders
 * the same screen as each piece of work lands, and the visitor watches the pile
 * grow. It lives on the CANVAS, not the panel, because that is where the visitor
 * is already looking and where the findings will land.
 *
 * These are completed steps, so they read as a settled list rather than spinners:
 * a rule, the count, and the lines. `aria-live` announces new ones without
 * stealing focus.
 */
const Milestones: FC<{ lines: string[] }> = ({ lines }) => {
  const intl = useIntl();
  return (
    <div className="mb-9">
      <CanvasSectionLabel>{intl.formatMessage(messages.milestonesLabel)}</CanvasSectionLabel>
      <ul aria-live="polite" className="mt-3 flex flex-col gap-2">
        {lines.map((line) => (
          <li
            key={line}
            className="flex min-w-0 animate-fadeUp items-baseline gap-2.5 motion-reduce:animate-none"
          >
            <Check aria-hidden="true" className="mt-[0.15rem] h-3 w-3 flex-none text-canvas-meta" />
            <span className="max-w-[70ch] text-sm leading-relaxed text-canvas-ink">{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The live brief — the canvas during UNDERSTAND.
 *
 * This is the customer watching themselves be understood, so it deliberately does
 * NOT talk: no prose, no interpretation, no questions. Just the rows the agent has
 * actually established, each appearing as it is learned. The conversation is in the
 * panel; this is the record of it.
 *
 * AND IT IS CORRECTABLE. A wrong brief row poisons every name that follows, and
 * the customer can see it is wrong the moment it appears — so fixing it costs one
 * click and one line, not a re-run of the interview. It stays a reflection, never
 * a form: rows are read-only text until the customer chooses to touch one, there
 * are no empty fields inviting input, and nothing is ever required.
 */
const Brief: FC<{ rows: BriefRow[]; quiet?: boolean; correctable?: boolean }> = ({
  rows,
  quiet = false,
  correctable = false,
}) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const [editing, setEditing] = useState<string | undefined>(undefined);
  const [draft, setDraft] = useState('');

  if (rows.length === 0) {
    return (
      <p className="max-w-[46ch] text-sm leading-relaxed text-canvas-meta/70">
        {intl.formatMessage(messages.briefEmpty)}
      </p>
    );
  }

  const commit = (row: BriefRow) => {
    const next = draft.trim();
    setEditing(undefined);
    if (next.length > 0 && next !== row.value) {
      dispatch?.('reviseBrief', { label: row.label, value: next });
    }
  };

  return (
    <section>
      <dl className={cn('grid gap-x-10', quiet ? 'gap-y-3 sm:grid-cols-2' : 'gap-y-7')}>
        {rows.map((row) => {
          const isEditing = correctable && editing === row.label;
          return (
            <div
              key={`${row.label}-${row.value}`}
              className="min-w-0 animate-fadeUp motion-reduce:animate-none"
            >
              <dt className={canvasLabelClass('field')}>{row.label}</dt>
              <dd
                className={cn(
                  'mt-2 max-w-[52ch] leading-snug text-canvas-ink',
                  quiet ? 'text-sm leading-relaxed' : 'text-xl font-medium sm:text-2xl',
                )}
              >
                {isEditing ? (
                  <span className="flex flex-col gap-2">
                    {/* Deliberately a textarea-free single line: a correction is a
                        replacement of one line, not an essay. */}
                    <input
                      autoFocus
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commit(row);
                        }
                        if (event.key === 'Escape') {
                          setEditing(undefined);
                        }
                      }}
                      className={cn(
                        'w-full rounded-sm border border-canvas-meta/50 bg-canvas/60 px-2.5 py-1.5 text-canvas-ink outline-none focus:border-canvas-ink',
                        quiet ? 'text-sm' : 'text-lg font-medium sm:text-xl',
                      )}
                    />
                    <span className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => commit(row)}
                        className="touch-manipulation rounded-sm border border-canvas-ink bg-canvas-ink px-2.5 py-1 text-[11px] font-semibold uppercase tracking-caps text-canvas transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta"
                      >
                        {intl.formatMessage(messages.saveBriefRow)}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(undefined)}
                        className="touch-manipulation text-[11px] font-medium text-canvas-meta/80 underline underline-offset-2 hover:text-canvas-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta"
                      >
                        {intl.formatMessage(messages.cancelBriefRow)}
                      </button>
                    </span>
                  </span>
                ) : correctable ? (
                  /* The row is still text. The affordance is the row itself, with
                     a dotted underline that only shows up on hover — no pencil
                     icons cluttering the canvas. */
                  <button
                    type="button"
                    aria-label={`${intl.formatMessage(messages.editBriefRow)}: ${row.label}`}
                    onClick={() => {
                      setDraft(row.value);
                      setEditing(row.label);
                    }}
                    className="-mx-1 max-w-full rounded-sm px-1 text-left decoration-canvas-meta/40 decoration-dotted underline-offset-4 transition-colors duration-150 hover:bg-canvas-meta/[0.06] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta"
                  >
                    {row.value}
                  </button>
                ) : (
                  row.value
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      {correctable ? (
        <p className="mt-7 text-xs leading-relaxed text-canvas-meta/70">
          {intl.formatMessage(messages.briefEditHint)}
        </p>
      ) : null}
    </section>
  );
};

export const StudioFrame: FC<A2uiNodeViewProps> = observer(({ node, renderChildren }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const messagesStore = useMessagingStore();

  const activity = str(node.props.activity);
  const currently = optStr(node.props.currently);
  const note = optStr(node.props.note);
  const progress = optStr(node.props.progress);
  const question = optStr(node.props.question);
  const invite = optStr(node.props.invite);
  const established = arr(node.props.established).filter(
    (line): line is string => typeof line === 'string' && line.length > 0,
  );
  const milestones = arr(node.props.milestones).filter(
    (line): line is string => typeof line === 'string' && line.length > 0,
  );
  const options = arr(node.props.options).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    const id = str(record.id);
    const label = str(record.label);
    return id && label ? [{ id, label, detail: optStr(record.detail) }] : [];
  });

  // Real evidence only: the live run state, whether a question is open, and
  // whether a decision was actually written to the canvas (derived server-side
  // from the blocks the model wrote — see surfaces/studio.ts).
  const generating = messagesStore.userRequestPending;
  const state: StudioUiState = deriveStudioState({
    screen: 'studio',
    generating,
    hasQuestion: question !== undefined,
    hasDecision: node.props.decided === true,
  });

  // Publish the phase for the header's UNDERSTAND / DISCOVER / DECIDE indicator.
  // The phase is computed server-side from the blocks the agent actually wrote,
  // and only ever advances (studio-state.ts).
  const phase = readPhaseProp(node.props.phase);
  useEffect(() => {
    markStudioEngaged();
    setStudioPhase(phase);
  }, [phase]);

  // THE HIERARCHY FLIP. While the brief is still being understood the panel is the
  // conversation and the canvas is a quiet mirror of what has been established;
  // once there is work to show, the canvas is the main event and the panel steps
  // back. It keys on whether this screen HAS blocks — not on the phase, because a
  // late `reading` or `insight` block derives `understand` and would otherwise be
  // dropped from the canvas without a trace.
  const understanding = node.props.hasBlocks !== true;
  const brief = readLines(node.props.brief);

  return (
    <div
      data-studio-state={state}
      className="mx-auto flex w-full max-w-[1560px] flex-col gap-3 overflow-x-hidden lg:flex-row lg:items-start lg:gap-4"
    >
      {/* Mobile: presence first, so the agent is never invisible. */}
      <div className="flex flex-col gap-3 lg:hidden">
        <Presence state={state} activity={activity} />
        {state === 'working' && currently ? <CurrentlyLine currently={currently} /> : null}
      </div>

      {/* The live output canvas: one oversized active output field. Chrome labels
          in the corners (black small caps) frame it as an instrument readout; the
          output itself is the only thing in white. */}
      <section className="canvas-gradient flex min-w-0 flex-col rounded-[14px] border border-canvas-meta/15 bg-canvas px-6 py-7 text-canvas-ink shadow-elevated sm:px-10 sm:py-9 lg:min-h-[34rem] lg:basis-[70%] lg:px-14 lg:py-12">
        <header className="flex items-baseline justify-between gap-4">
          <CanvasFrameLabel>
            {intl.formatMessage(understanding ? messages.briefCorner : messages.agentOutput)}
          </CanvasFrameLabel>
          <CanvasFrameLabel>
            {intl.formatMessage(
              understanding
                ? messages.listening
                : generating
                  ? messages.liveStreaming
                  : messages.liveIdle,
            )}
          </CanvasFrameLabel>
        </header>

        <div className="mt-9 flex-1">
          {/* UNDERSTAND: the canvas is the live brief and nothing else. It reflects,
              it does not talk — the conversation is in the panel. */}
          {understanding ? (
            <>
              <Brief rows={brief} correctable />
              {/* The first working turn is still `understand` — no blocks have been
                  written yet — but the agent may already be running several
                  searches. Without this the canvas would sit silent behind a
                  cursor on exactly the turn the visitor is least sure anything is
                  happening. */}
              {milestones.length > 0 ? (
                <div className="mt-9 border-t border-canvas-meta/25 pt-7">
                  <Milestones lines={milestones} />
                </div>
              ) : null}
            </>
          ) : (
            <>
              {/* ESTABLISHED — what is already agreed with the customer: the
                  objective, who it is for, the taste they confirmed, the hard
                  constraints. It is deliberately QUIET and stable: softer ink,
                  smaller type, no animation, no interaction. Nothing here should
                  look like it is still moving, because none of it is. */}
              {brief.length > 0 || established.length > 0 ? (
                <div className="mb-9 border-b border-canvas-meta/25 pb-7">
                  <CanvasSectionLabel>
                    {intl.formatMessage(messages.settledTitle)}
                  </CanvasSectionLabel>
                  {brief.length > 0 ? (
                    <div className="mt-4">
                      <Brief rows={brief} quiet />
                    </div>
                  ) : null}
                  {established.length > 0 ? (
                    <ul className="mt-4 flex flex-col gap-1.5">
                      {established.map((line) => (
                        <li
                          key={line}
                          className="max-w-[70ch] text-sm leading-relaxed text-canvas-meta"
                        >
                          {line}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}

              {/* IN PROGRESS — the live half: what is still changing. The counts
                  sit directly under the title as its status line, one rank down
                  (field weight), so the title owns the region and the numbers
                  report inside it. */}
              <CanvasSectionLabel>{intl.formatMessage(messages.liveTitle)}</CanvasSectionLabel>
              {progress ? (
                <CanvasFieldLabel className="mt-2.5 block tabular-nums">
                  {progress}
                </CanvasFieldLabel>
              ) : null}

              {/* Reading order: what I have done, then what I found. While the
                  research is still running the milestones are the ONLY thing in
                  this region, which is exactly their job. */}
              <div className="mt-7">
                {milestones.length > 0 ? <Milestones lines={milestones} /> : null}

                <div className="flex flex-col gap-12">{renderChildren()}</div>
              </div>
            </>
          )}

          {/* The cursor follows the live output: mounted only while the run is
              genuinely in flight, and never beside finished text. What the agent
              is DOING is said in the panel, not repeated here. */}
          {generating ? (
            <p className="mt-6 leading-none">
              <Cursor />
            </p>
          ) : null}
        </div>

        <InProgressBriefActions generating={generating} />
        <footer className="mt-12 flex items-baseline justify-between gap-4">
          <CanvasFrameLabel>{intl.formatMessage(messages.outputField)}</CanvasFrameLabel>
          <CanvasFrameLabel>{intl.formatMessage(messages.realTime)}</CanvasFrameLabel>
        </footer>
      </section>

      {/* The agent panel: presence, what the agent is doing, and — only when the
          agent actually needs human judgement — the interaction. */}
      <aside className="flex min-w-0 flex-col gap-6 rounded-lg border border-panel-line bg-panel px-6 py-8 text-panel-ink lg:basis-[30%] lg:px-7">
        <Presence state={state} activity={activity} className="hidden lg:flex" />

        {/* UNDERSTAND: the panel is the conversation, so the agent's read stays a
            quiet aside and the question below it is the loudest thing on screen.
            Once working, the read becomes the panel's headline statement. */}
        {note ? (
          <div className="flex flex-col gap-2 border-t border-panel-line pt-6">
            <span className="text-[10px] font-semibold uppercase leading-none tracking-caps text-muted-foreground-subtle">
              {intl.formatMessage(messages.agentNow)}
            </span>
            <p
              className={cn(
                'max-w-[42ch]',
                understanding
                  ? 'text-sm leading-relaxed text-muted-foreground'
                  : 'text-lg font-medium leading-snug text-panel-ink',
              )}
            >
              “{note}”
            </p>
          </div>
        ) : null}

        {state === 'working' && currently ? <CurrentlyLine currently={currently} /> : null}

        {/* The judgement question belongs to `needs_input` (and to a finished
            `complete` screen). While a run is in flight the ball is with the
            agent, so a question from the previous turn is not left sitting there
            inviting a second answer. */}
        {question && state !== 'working' ? (
          <div className="flex flex-col gap-4 border-t border-panel-line pt-6">
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold uppercase leading-none tracking-caps text-muted-foreground-subtle">
                {intl.formatMessage(
                  understanding ? messages.overToYou : messages.yourJudgment,
                )}
              </span>
              <p
                className={cn(
                  'max-w-[42ch] text-panel-ink',
                  understanding
                    ? 'text-lg font-medium leading-snug sm:text-xl'
                    : 'text-sm leading-relaxed',
                )}
              >
                {question}
              </p>
            </div>

            {/* WHEN THE AGENT WANTS THEIR OWN WORDS, WRITING COMES FIRST.
                New naming material — a family saying, a place, an object — can
                only come from them, and a row of preset buttons with a text field
                underneath makes typing look like the leftover option nobody picks.
                So `invite` flips the order: the invitation and the field go above,
                and the suggestions demote to a prompt for someone who cannot think
                of anything. */}
            {invite ? (
              <div className="flex flex-col gap-2.5">
                <p className="max-w-[42ch] text-sm leading-relaxed text-muted-foreground">
                  {invite}
                </p>
                <PanelInput />
              </div>
            ) : null}

            {options.length > 0 ? (
              <div className="flex flex-col gap-2">
                {invite ? (
                  <span className="text-xs text-muted-foreground-subtle">
                    {intl.formatMessage(messages.writeInstead)}
                  </span>
                ) : null}
                {options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() =>
                      dispatch?.('answer', { optionId: option.id, label: option.label })
                    }
                    /* White interaction outlines: green is the canvas, not the
                       hover colour. */
                    className="touch-manipulation rounded-sm border border-panel-line bg-transparent px-4 py-3 text-left transition-colors duration-150 hover:border-panel-ink hover:bg-panel-ink/[0.04] focus-visible:border-panel-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[3px] focus-visible:outline-panel-ink/60 active:scale-[0.99]"
                  >
                    <span className="block text-sm font-medium text-panel-ink">{option.label}</span>
                    {option.detail ? (
                      <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                        {option.detail}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Free text, in the panel, only while the ball is with the customer —
            and not a second time when the invitation already put it above the
            options. */}
        {state !== 'working' && !(invite && question) ? (
          <div
            className={cn(
              'flex flex-col gap-3',
              question ? '' : 'border-t border-panel-line pt-6',
            )}
          >
            {/* CONTINUING SHOULD NOT COST A SENTENCE.
                When the agent hands back mid-search with nothing to decide, the
                only honest reply is "carry on" — and typing that is friction that
                teaches the visitor their input is required when it is not. So the
                turn can be handed straight back.

                It appears ONLY in that exact state: work already on the canvas
                (`!understanding`), no judgement open (`!question`), and the
                engagement still live (`needs_input` excludes the decided screen).
                The moment the agent judges the strategy spent it asks a real
                question — with `invite` when it needs their own material — and
                this disappears, because at that point "keep going" is precisely
                the wrong answer and the choice genuinely belongs to them. */}
            {offersKeepExploring({
              state,
              hasQuestion: question !== undefined,
              hasGround: !understanding || brief.length > 0,
            }) ? (
              <button
                type="button"
                onClick={() => dispatch?.('keepExploring', {})}
                className="group flex touch-manipulation items-center gap-2 self-start rounded-sm border border-panel-line bg-transparent px-4 py-2.5 text-left transition-colors duration-150 hover:border-panel-ink hover:bg-panel-ink/[0.04] focus-visible:border-panel-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[3px] focus-visible:outline-panel-ink/60 active:scale-[0.99]"
              >
                <span className="text-sm font-medium text-panel-ink">
                  {intl.formatMessage(messages.keepExploring)}
                </span>
                <ArrowRight
                  aria-hidden="true"
                  className="size-3.5 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-panel-ink motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                />
              </button>
            ) : null}
            <PanelInput />
          </div>
        ) : null}
      </aside>
    </div>
  );
});

/** Where a finding came from. Shared by the ordinary blocks and the plaque. */
const SourceList: FC<{ sources: Source[] }> = ({ sources }) => (
  <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-1">
    {sources.map((source) => (
      <li key={`${source.label}-${source.url ?? ''}`} className="min-w-0 text-xs">
        {source.url ? (
          <a
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className="text-canvas-meta/80 underline underline-offset-2 hover:text-canvas-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta"
          >
            {source.label}
          </a>
        ) : (
          <span className="text-canvas-meta/70">{source.label}</span>
        )}
      </li>
    ))}
  </ul>
);

/** What to actually do now the name is chosen. A numbered list in the canvas's
 *  own type — practical steps, deliberately NOT a row of call-to-action buttons,
 *  because none of these happen inside this product. */
const NextSteps: FC<{ steps: string[] }> = ({ steps }) => {
  const intl = useIntl();
  return (
    <div className="mt-10 border-t border-canvas-meta/25 pt-7">
      <CanvasSectionLabel>{intl.formatMessage(messages.nextSteps)}</CanvasSectionLabel>
      <ol className="mt-4 flex flex-col gap-3">
        {steps.map((step, index) => (
          <li key={step} className="flex min-w-0 items-baseline gap-3.5">
            <span className="text-xs font-medium tabular-nums leading-relaxed text-canvas-meta">
              {index + 1}
            </span>
            <span className="max-w-[64ch] text-sm leading-relaxed text-canvas-ink">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

/** One finished piece of output. Candidates are specimens: the name is the
 *  largest thing on the canvas, and the research under it stays functional.
 *
 *  A `selection` block is the exception and the ENDING: the name the customer
 *  actually chose, set as a plaque. It is the same canvas, the same ink and the
 *  same type — the ceremony comes from space and scale, not from confetti, a badge
 *  or a colour nothing else on the screen uses. */
export const StudioBlock: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();

  const kind = optStr(node.props.kind);
  const heading = str(node.props.heading);
  const body = optStr(node.props.body);
  const name = optStr(node.props.name);
  const nameKind = optStr(node.props.nameKind);
  const pronunciation = optStr(node.props.pronunciation);
  const meaning = optStr(node.props.meaning);
  const lines = readLines(node.props.lines);
  const sources = readSources(node.props.sources);
  const confidence = readConfidence(node.props.confidence);
  const ruledOut = confidence === 'ruled-out';
  const nextSteps = arr(node.props.nextSteps).filter(
    (step): step is string => typeof step === 'string' && step.length > 0,
  );
  const selected = kind === 'selection';
  const briefId = optStr(node.props.briefId);
  const kindMessage =
    nameKind === 'real-word' || nameKind === 'derived' || nameKind === 'invented'
      ? KIND_MESSAGES[nameKind]
      : undefined;

  // THE DELIVERABLE. A `report` block carrying a briefId is not a block of prose
  // at all — it is the document the customer keeps, read from storage so the
  // screen, the download and the emailed copy are one object rather than three
  // retyped versions that can disagree about a finding.
  if (kind === 'report' && briefId) {
    return <BriefDeliverable briefId={briefId} />;
  }

  // THE PLAQUE. Its own branch, because everything about it is different: the
  // eyebrow, the name at display scale, and a hairline frame that says "this is
  // settled" — while the evidence underneath keeps its honest statuses, so the
  // moment stays credible instead of turning into a certificate.
  if (selected && name) {
    return (
      <article className="min-w-0 animate-fadeUp motion-reduce:animate-none">
        <div className="rounded-[10px] border border-canvas-meta/30 px-6 py-10 text-center sm:px-10 sm:py-14">
          <CanvasFieldLabel className="block">
            {intl.formatMessage(messages.youFoundIt)}
          </CanvasFieldLabel>
          <h3
            className="mt-7 text-5xl font-bold leading-[0.95] tracking-tight text-canvas-ink sm:text-7xl"
            translate="no"
          >
            {name}
          </h3>
          {pronunciation ? (
            <p className="mt-3 text-sm text-canvas-meta/80" translate="no">
              {pronunciation}
            </p>
          ) : null}
          <CanvasSectionLabel className="mt-7 block">
            {intl.formatMessage(messages.selectedName)}
          </CanvasSectionLabel>
          {kindMessage || meaning ? (
            <p className="mx-auto mt-6 max-w-[60ch] text-sm leading-relaxed text-canvas-meta/80">
              {kindMessage ? (
                <CanvasFieldLabel className="mr-2">
                  {intl.formatMessage(kindMessage)}
                </CanvasFieldLabel>
              ) : null}
              {meaning}
            </p>
          ) : null}
          {body ? (
            <>
              <hr
                className="mx-auto mt-9 w-16 border-0 border-t border-canvas-meta/40"
                aria-hidden="true"
              />
              <p className="mx-auto mt-9 max-w-[58ch] text-lg leading-relaxed text-canvas-ink sm:text-xl">
                {body}
              </p>
            </>
          ) : null}
        </div>

        {/* The screening status, unchanged and un-softened: a plaque is not a
            clearance certificate, so an unresolved check still reads unresolved
            right here at the end. */}
        {lines.length > 0 ? (
          <dl className="mt-9 grid gap-x-10 gap-y-4 sm:grid-cols-2">
            {lines.map((line) => (
              <div key={`${line.label}-${line.value}`} className="min-w-0">
                <dt className={cn('flex flex-wrap items-center gap-2', canvasLabelClass('field'))}>
                  {line.label}
                  {line.status ? <StatusTag status={line.status} /> : null}
                </dt>
                <dd className="mt-1 text-sm leading-relaxed text-canvas-ink">{line.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {nextSteps.length > 0 ? <NextSteps steps={nextSteps} /> : null}

        {sources.length > 0 ? <SourceList sources={sources} /> : null}
      </article>
    );
  }

  return (
    <article className="min-w-0 animate-fadeUp motion-reduce:animate-none">
      {name ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            {/* A RULED-OUT NAME IS SHOWN FOR DIRECTION, NEVER AS AN OPTION.
                It keeps the specimen size, because the point is that the customer
                can see and react to where the thinking is going — but it is dimmed
                and badged, so it can never be mistaken for something they could
                actually have. The reason it died is a required evidence row. */}
            <h3
              className={cn(
                'text-4xl font-bold leading-none tracking-tight sm:text-5xl',
                ruledOut ? 'text-canvas-ink/40' : 'text-canvas-ink',
              )}
              translate="no"
            >
              {name}
            </h3>
            {ruledOut ? (
              <span className="rounded-[3px] border border-canvas-ink/40 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-caps text-canvas-ink/60">
                {intl.formatMessage(messages.exploratoryOnly)}
              </span>
            ) : null}
            {pronunciation ? (
              <span className="text-sm text-canvas-meta/70" translate="no">
                {pronunciation}
              </span>
            ) : null}
          </div>
          {kindMessage || meaning ? (
            <p className="mt-3 max-w-[70ch] text-sm leading-relaxed text-canvas-meta/80">
              {kindMessage ? (
                <CanvasFieldLabel className="mr-2">
                  {intl.formatMessage(kindMessage)}
                </CanvasFieldLabel>
              ) : null}
              {meaning}
            </p>
          ) : null}
          {/* Confidence sits under the specimen, beside the classification, so a
              name that LOOKS finished can never look more settled than its
              evidence actually is. */}
          {confidence ? <ConfidenceTag confidence={confidence} className="mt-3" /> : null}
        </>
      ) : (
        /* A reading/territory/decision block: the small caps label is BLACK
           chrome and sits at SECTION rank — a step under the canvas frame
           labels, so the frame still reads as the outermost layer. The large
           statement under it is the white output. */
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CanvasSectionLabel className="block">{heading}</CanvasSectionLabel>
          {confidence ? <ConfidenceTag confidence={confidence} /> : null}
        </div>
      )}

      {body ? (
        <>
          {/* The black divider separates the useful headline above from the
              agent's output below. */}
          <hr className="mt-5 border-0 border-t border-canvas-meta/25" aria-hidden="true" />
          <p
            className={cn(
              'max-w-[68ch] leading-relaxed text-canvas-ink',
              name ? 'mt-5 text-base' : 'mt-5 text-xl font-medium sm:text-2xl',
            )}
          >
            {body}
          </p>
        </>
      ) : null}

      {lines.length > 0 ? (
        <dl
          className={cn(
            'mt-6 grid gap-x-10 gap-y-3',
            /* The report's sections are prose, not one-line signals, so they read
               down a single column instead of being squeezed into two. */
            kind === 'report' ? 'gap-y-5' : 'sm:grid-cols-2',
          )}
        >
          {lines.map((line) => (
            <div key={`${line.label}-${line.value}`} className="min-w-0">
              <dt className={cn('flex flex-wrap items-center gap-2', canvasLabelClass('field'))}>
                {line.label}
                {/* The status sits on the LABEL, not the value: it qualifies what
                    kind of claim this row is, and an unfinished check must never be
                    readable as a clean one. */}
                {line.status ? <StatusTag status={line.status} /> : null}
              </dt>
              <dd
                className={cn(
                  'mt-1 leading-relaxed text-canvas-ink',
                  kind === 'report' ? 'max-w-[74ch] text-sm' : 'text-sm',
                )}
              >
                {line.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {nextSteps.length > 0 ? <NextSteps steps={nextSteps} /> : null}

      {sources.length > 0 ? <SourceList sources={sources} /> : null}

      {name ? (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {/* A RULED-OUT NAME OFFERS NO CHOICE. "This one" on a name that failed
              screening would invite the customer to pick something they cannot
              have — the exact confusion the dimmed treatment exists to prevent.
              The directional reaction for these lives in the panel question, so
              here only "tell me more" remains, which is always honest. */}
          {ruledOut
            ? null
            : (
                [
                  ['love', messages.loveIt],
                  ['maybe', messages.maybe],
                  ['no', messages.notThis],
                ] as const
              ).map(([reaction, message]) => (
                <button
                  key={reaction}
                  type="button"
                  onClick={() => dispatch?.('reactToName', { name, reaction })}
                  className="touch-manipulation rounded-sm border border-canvas-meta/35 px-3 py-1.5 text-xs font-medium text-canvas-meta transition-colors duration-150 hover:border-canvas-meta hover:bg-canvas-meta/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta active:scale-95"
                >
                  {intl.formatMessage(message)}
                </button>
              ))}
          <button
            type="button"
            onClick={() => dispatch?.('explainName', { name })}
            className="touch-manipulation px-1 py-1.5 text-xs text-canvas-meta/80 underline underline-offset-2 transition-colors duration-150 hover:text-canvas-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-canvas-meta"
          >
            {intl.formatMessage(messages.tellMeMore)}
          </button>
        </div>
      ) : null}
    </article>
  );
};

/** The contract's `component` name resolves here; `compose` replaces it with the
 *  frame + block tree, so this is only the atomic-fallback entry. */
export const Studio = StudioFrame;
